import '../styles/globals.css';
import { ChakraProvider } from '@chakra-ui/react';
import {
  ApolloProvider,
} from '@apollo/client';
import { AppProps /* , AppContext */ } from 'next/app';
import { ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import MainLayout from '../components/layouts/main';
import DefaultLayout from '../components/layouts/default';
import { makeClient } from '../lib/apollo-client';
import { UserProvider } from './api/userAuth';
import theme from '../styles/defaultTheme';
import { LoaderProvider } from '../lib/contexts/LoaderContext';
import Loader from '../components/loader/index';

import initSentry from '../utils/withSentry';
import { NavigationProvider } from '../lib/contexts/NavigationContext';
import { PreviewProvider } from '../lib/contexts/PreviewContext';
import PreviewStateSyncer from '../components/PreviewStateSyncer';
import { InventoryAuthProvider } from '../lib/inventory/InventoryAuthContext';
import InventoryLayout from '../components/inventory/InventoryLayout';
import envVariables from '../lib/envVariables';

// Define page titles (consider moving this to a config file)
const pageTitles: { [key: string]: string } = {
  '/': 'UKCPA Home', // Adjusted default title
  '/about': 'About Us - UKCPA',
  '/contact': 'Contact - UKCPA',
  '/class-booking': 'Class Booking - UKCPA', // Added class booking title
  // Add other routes as needed
};

const defaultTitle = 'UKCPA'; // Fallback title

initSentry();

// Use standard AppProps instead of custom IAppProps
/*
type IAppProps = {
  Component: NextComponentType,
  children: NextComponentType,
  pageProps: any
}
*/

// Use AppProps from next/app
const UKCPAApp = ({ Component, pageProps }: AppProps): ReactElement => {
  const apolloClient = makeClient();
  const isInventorySite = envVariables.SITE_ID === 'INVENTORY';
  const router = useRouter(); // Get router instance
  const [pageTitle, setPageTitle] = useState(defaultTitle); // State for the title

  // Effect to update title state on route change
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      console.log('Route changed to:', url);
      try {
        const currentPath = url.split('?')[0]; // Basic path extraction
        console.log('Using path:', currentPath);
        const title = pageTitles[currentPath] || defaultTitle;
        console.log('Setting title state:', title);
        setPageTitle(title); // Update state instead of document.title
      } catch (error) {
        console.error('Error setting title state:', error);
        setPageTitle(defaultTitle); // Fallback on error
      }
    };

    // Set initial title based on the initial path
    console.log('Initial path check:', router.asPath);
    handleRouteChange(router.asPath);

    // Subscribe to route changes
    router.events.on('routeChangeComplete', handleRouteChange);

    // Cleanup subscription on unmount
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // Dependency array only needs router.events and router.asPath
    // as handleRouteChange is defined within the effect
  }, [router.events, router.asPath]);

  return (
    <>
      {/* Set title using next/head */}
      <Head>
        <title>{pageTitle}</title>
        {/* You can add other default head elements here if needed */}
      </Head>
      <ChakraProvider theme={theme}>
        <ApolloProvider client={apolloClient}>
          <InventoryAuthProvider>
            <NavigationProvider>
              <UserProvider>
                <PreviewProvider>
                  <PreviewStateSyncer />
                  {isInventorySite ? (
                    <InventoryLayout>
                      <Component {...pageProps} />
                    </InventoryLayout>
                  ) : (
                    <MainLayout>
                      <LoaderProvider>
                        <DefaultLayout>
                          <Component {...pageProps} />
                        </DefaultLayout>
                        <Loader />
                      </LoaderProvider>
                    </MainLayout>
                  )}
                </PreviewProvider>
              </UserProvider>
            </NavigationProvider>
          </InventoryAuthProvider>
        </ApolloProvider>
      </ChakraProvider>
    </>
  );
};

export default UKCPAApp;
